import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, FormGroup, Button } from 'reactstrap';
import { DatePicker } from 'components/common';
import { formatDate, formatDateTime, getTextColor } from 'services/util';
import styles from "assets/scss/task_management.module.css";
import { FaTimes } from 'react-icons/fa';
import { LocalForm } from 'react-redux-form';
import InputFile from 'components/common/InputFile';
import { Trans } from 'react-i18next';
import Select from 'react-select';
import { Multiselect } from "multiselect-react-dropdown";

function UpdateTask({ task, onClose, onUpdate, availableEmployees, fetchLabels, groups, buckets, userPermission, activeTab, t }) {
    const [updatedTaskName, setUpdatedTaskName] = useState(task.bucket_task_name);
    const [assignedEmployees, setAssignedEmployees] = useState(task?.employee || []);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [progress, setProgress] = useState(task?.progress);
    const [priority, setPriority] = useState(task?.priority);
    const [updatedTaskStartDate, setUpdatedTaskStartDate] = useState(task.start_date ? new Date(task.start_date) : null);
    const [updatedTaskDueDate, setUpdatedTaskDueDate] = useState(task.end_date ? new Date(task.end_date) : null);
    const [repeat, setRepeat] = useState(task?.repeat);
    const [notes, setNotes] = useState(task?.notes || '');
    const [attachments, setAttachments] = useState({});
    const [comments, setComments] = useState(task?.comments || '');
    const [selectedEmployees, setSelectedEmployees] = useState(task?.employee || []);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [showDropdown, setShowDropdown] = useState(false);
    const [description, setDescription] = useState(task?.description || '');
    const [selectedGroup, setSelectedGroup] = useState(task?.employees_group || '');
    const [selectedBucket, setSelectedBucket] = useState(task?.planner_bucket || '');
    const [isLabelChange, setEditLabel] = useState(false);
    const [allowUpdate, setAllowUpdate] = useState(task.allow_update || false);
    const labelOptions = fetchLabels?.map(label => ({
        value: label.id,
        label: label.label_name,
        color_code: label.color_code,
    }));

    const defaultOptions = task?.task_label?.map(id => {
        const label = labelOptions?.find(label => label.value === id);
        return { value: id, label: label?.label, color_code: label?.color_code || '', textColor: getTextColor(label?.color_code || '') };
    });

    useEffect(() => {
        setAssignedEmployees(task?.employee || []);
        setSelectedEmployees(task?.employee || []);
        setSelectedGroup(task?.employees_group || null);
        setSelectedBucket(task?.planner_bucket || null);
        setSelectedOptions(defaultOptions);
    }, [task, fetchLabels]);

    const handleAssignButtonClick = () => {
        setShowDropdown(!showDropdown);
    };

    const handleEmployeeSelect = (employeeId) => {
        if (selectedEmployees.includes(employeeId)) {
            setSelectedEmployees(selectedEmployees.filter(id => id !== employeeId));
        } else {
            setSelectedEmployees([...selectedEmployees, employeeId]);
        }
    };

    const handleRemoveEmployee = (employeeId) => {
        setSelectedEmployees(selectedEmployees.filter(id => id !== employeeId));
        setAssignedEmployees(assignedEmployees.filter(id => id !== employeeId));
    };

    const groupOptions = groups.map(group => ({
        value: group.id,
        label: group.group_name
    }));

    const bucketOptions = buckets.map(bucket => ({
        value: bucket.id,
        label: bucket.bucket_name
    }));

    const handleGroupChange = selectedOption => {
        setSelectedGroup(selectedOption.value);
    };


    const handleRemoveGroup = () => {
        setSelectedGroup('');
    };

    const handleBucketChange = selectedOption => {
        setSelectedBucket(selectedOption.value);
    };

    const handleUpdate = () => {
        const updatedTask = {
            ...task,
            bucket_task_name: updatedTaskName,
            start_date: updatedTaskStartDate ? formatDate(updatedTaskStartDate) : null,
            end_date: updatedTaskDueDate ? formatDate(updatedTaskDueDate) : null,
            employee: selectedEmployees,
            task_label: selectedOptions.map(label => label.value),
            planner_bucket: selectedBucket,
            progress: progress,
            priority: priority,
            repeat: repeat,
            notes: notes,
            attachment: attachments.attachment,
            comments: comments,
            added_by: task?.added_by,
            description: description,
            employees_group: selectedGroup,
            allow_update: allowUpdate
        };
        onUpdate(updatedTask);
        onClose();
    };
    

    const selectedGroupObject = groups.find(group => group.id === selectedGroup);

    const selectedBucketObject = buckets.find(bucket => bucket.id === selectedBucket);

    const handleSelect = (selectedLabels) => {
        setSelectedOptions(selectedLabels);
      };

      const handleRemove = (selectedLabels) => {
        setSelectedOptions(selectedLabels);
      };

      const multiSelectStyle = {
        chips: { background: '#72B566' },
    };

    return (
        <Modal isOpen={true} toggle={onClose} className={styles.update_task_modal} style={{ maxWidth: '800px', minWidth: '300px' }}>
            <ModalHeader toggle={onClose}>
                <div className={styles.modal_header_content}>
                    <span className={styles.task_title}><Trans>Update Task</Trans></span>
                </div>
            </ModalHeader>
            <ModalBody style={{ margin: '0 30px' }}>
                <div className={styles.form_group}>
                    {progress==="Completed" ? (
                        <input type="text" className={`${styles.title_input} ${styles.inputField} text-decoration-line-through`} value={updatedTaskName} onChange={(e) => setUpdatedTaskName(e.target.value)} />
                    ): <input type="text" className={`${styles.title_input}`} value={updatedTaskName} onChange={(e) => setUpdatedTaskName(e.target.value)} />}
                    
                </div>
                <div className={styles.form_group}>
                    <p><Trans>Last changed</Trans>: {formatDateTime(task.updated_at)}</p>
                    </div>
                    {activeTab==='teamTasks' && userPermission && (
                        <div>
                            <label><Trans>Assigned Employees</Trans></label>
                            <div className="d-flex flex-wrap">
                            {[...assignedEmployees].map(employeeId => {
                                const employee = availableEmployees.find(employee => employee.id === employeeId);
                                return (
                                    <div key={employeeId} className="d-flex align-items-center justify-content-center bg-success text-white m-2 p-2 rounded">
                                        {assignedEmployees.length > 2 
                                            ? employee?.full_name.slice(0, 2) 
                                            : employee?.full_name}
                                        <FaTimes onClick={() => handleRemoveEmployee(employeeId)} className="ml-2" />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
                {activeTab==='teamTasks' && userPermission && (
                    <div className={styles.assignButtonContainer}>
                        <button className={styles.assignButton} onClick={handleAssignButtonClick}><Trans>Assign</Trans></button>
                        {showDropdown && (
                            <div className={styles.dropdownContent}>
                                <div className={styles.notes_area}>
                                    <input
                                        type="text"
                                        placeholder={t("Search Employees")}
                                        value={searchKeyword}
                                        onChange={(e) => setSearchKeyword(e.target.value)}
                                        className={styles.search_employee}
                                    />
                                </div>
                                {selectedEmployees.length > 0 && (
                                    <>
                                        <div className={styles.assignedEmployees}><strong><Trans>Assigned</Trans></strong></div>
                                        {selectedEmployees.map(employeeId => (
                                            <button key={employeeId} className={styles.assignedEmployee}>
                                                {availableEmployees.find(employee => employee.id === employeeId)?.full_name} 
                                                <FaTimes onClick={() => handleRemoveEmployee(employeeId)} />
                                            </button>
                                        ))}
                                    </>
                                )}
                                <div className={styles.suggestions}><strong><Trans>Suggestions</Trans></strong></div>
                                {availableEmployees
                                    .filter(employee => !selectedEmployees.includes(employee.id) && employee.full_name.toLowerCase().includes(searchKeyword.toLowerCase()))
                                    .map((employee, index) => (
                                        <button
                                            key={index}
                                            className={styles.suggestion}
                                            onClick={() => {
                                                handleEmployeeSelect(employee.id);
                                                setSearchKeyword("");
                                            }}
                                        >
                                            {employee.full_name}
                                        </button>
                                    ))}
                            </div>
                        )}
                    </div>
                )}
                {!isLabelChange ?  
                  <div className='d-flex justify-content-start align-items-center'>
                    <div className={styles.form_group}>
                    <label><Trans>Selected Labels</Trans></label>
                    <div className={styles.selectedLabels}>
                        {defaultOptions?.map((i) => (
                            <span 
                                key={i.value} 
                                style={{
                                    borderRadius: '10%', 
                                    backgroundColor: i.color_code || '',
                                    marginRight: "3px",
                                    color: i.textColor
                                }} 
                                className='p-2'>
                                {i.label}
                            </span>
                        ))}
                    </div>
                </div>
                <div className='mx-auto'/>
                <div>
                  <Button size='sm' color='primary' onClick={() => setEditLabel(!isLabelChange)}><i className='fa fa-edit' /></Button>
                </div>
                </div>
                :
                <div className={styles.form_group}>
                    <label><Trans>Task Label</Trans></label>
                    <div className={styles.groupSelectContainer}>
                    <Multiselect
                        options={labelOptions}
                        selectedValues={selectedOptions}
                        style={multiSelectStyle}
                        onSelect={handleSelect}
                        onRemove={handleRemove}
                        displayValue="label"
                        emptyRecordMsg={'No options available'}
                        selectionLimit={10}
                        avoidHighlightFirstOption
                    />
                    </div>
                </div>
                }
                {activeTab==='teamTasks' && userPermission && (
                    <div className={styles.form_group}>
                        <label><Trans>Group</Trans></label>
                        <div className={styles.groupSelectContainer}>
                            {selectedGroup && (
                                <div className={styles.selectedGroup}>
                                    {selectedGroupObject?.group_name}
                                    <FaTimes onClick={handleRemoveGroup} />
                                </div>
                            )}
                            <Select
                                value={selectedGroup}
                                onChange={handleGroupChange}
                                options={groupOptions}
                                placeholder={t("Select Group")}
                            />
                        </div>
                    </div>
                )}
                <div className={styles.form_row}>
                    <div className={`${styles.selectField} ${styles.group_select} ${styles.notes_area}`}>
                        <label><Trans>Bucket</Trans></label>
                        <Select
                            value={selectedBucket}
                            onChange={handleBucketChange}
                            options={bucketOptions}
                            placeholder={t("Select Bucket")}
                        />
                    </div>
                    <div className={styles.form_group}>
                        <label><Trans>Progress</Trans></label>
                        <select className={`${styles.selectField} ${styles.notes_area}`} value={progress} onChange={(e) => setProgress(e.target.value)}>
                            <option value="Not Started">Not Started</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                        </select>
                    </div>
                    <div className={styles.form_group}>
                        <label><Trans>Priority</Trans></label>
                        <select className={`${styles.selectField} ${styles.notes_area}`} value={priority} onChange={(e) => setPriority(e.target.value)}>
                            <option value="Urgent">Urgent</option>
                            <option value="Important">Important</option>
                            <option value="Medium">Medium</option>
                            <option value="Low">Low</option>
                        </select>
                    </div>
                </div>
                <div className={styles.form_row}>
                    <div className={`${styles.dates_field} ${styles.form_group}`}>
                        <label><Trans>Start Date</Trans></label>
                        <LocalForm>
                            <DatePicker
                                onChange={(date) => setUpdatedTaskStartDate(date)}
                                type="date"
                                model=".date"
                                placeholderText={t("Select start date")}
                                selected={updatedTaskStartDate}
                                defaultValue={updatedTaskStartDate}
                            />
                        </LocalForm>
                    </div>
                    <div className={`${styles.dates_field} ${styles.form_group}`}>
                        <label><Trans>Due Date</Trans></label>
                        <LocalForm>
                            <DatePicker
                                onChange={(date) => setUpdatedTaskDueDate(date)}
                                type="date"
                                model=".date"
                                placeholderText={t("Select a Date")}
                                selected={updatedTaskDueDate}
                                defaultValue={updatedTaskDueDate}
                                className={`${styles.selectField} ${styles.notes_area}`}
                            />
                        </LocalForm>
                    </div>
                    <div className={styles.form_group}>
                        <label><Trans>Repeat</Trans></label>
                        <select className={`${styles.selectField} ${styles.notes_area}`} value={repeat} onChange={(e) => setRepeat(e.target.value)}>
                            <option value="Does not repeat">Does not repeat</option>
                            <option value="Weekdays">Week Days</option>
                            <option value="Daily">Every Day</option>
                            <option value="Weekly">Every Week</option>
                            <option value="Monthly">Every Month</option>
                            <option value="Yearly">Every Year</option>
                        </select>
                    </div>
                </div>
                <div className={styles.form_group}>
                    <label><Trans>Notes</Trans></label>
                    <textarea className={`${styles.notes_area} ${styles.wider_textarea}`} placeholder={t('Your Notes of the task here')} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
                <div className={styles.form_group}>
                    <label><Trans>Description</Trans></label>
                    <textarea className={styles.notes_area} placeholder={t('Your Description of the task here')} value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
                <div className={styles.form_group}>
                    <FormGroup className="pb-2 pt-3">
                        <label htmlFor=""><Trans>Attachment</Trans></label>
                        <InputFile file={attachments.attachment || task.attachment || ""} setData={setAttachments}></InputFile>
                    </FormGroup>
                </div>
                <div className={styles.form_group}>
                    <label><Trans>Comments</Trans></label>
                    <textarea className={styles.notes_area} placeholder={t('Your comments here')} value={comments} onChange={(e) => setComments(e.target.value)} />
                </div>
                <div className={styles.form_group}>
                    <label><Trans>Added By</Trans></label>
                    <div>{task?.added_by?.full_name}</div>
                    <div>{formatDateTime(task.created_at)}</div>
                </div>
                {userPermission && (
                    <div className={styles.form_group}>
                    <label><Trans>Allow Update</Trans></label>
                    <div className="form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="allowUpdateCheckbox"
                            checked={allowUpdate}
                            onChange={() => setAllowUpdate(!allowUpdate)}
                        />
                        <label className="form-check-label" htmlFor="allowUpdateCheckbox"><Trans>Allow</Trans></label>
                    </div>
                    <div className="text-muted text-sm">
                        <span>
                            <Trans>If you allow update, then assigned employee(s) will be able to change the labels, progress, add comments, and send re assign requests only.</Trans>
                        </span>
                    </div>
                </div>
                )}

            </ModalBody>
            <ModalFooter>
                <button onClick={handleUpdate} className="btn btn-primary"><Trans>Update Task</Trans></button>
                <button onClick={onClose} className="btn btn-secondary"><Trans>Cancel</Trans></button>
            </ModalFooter>
        </Modal>
    );
}

export default UpdateTask;